import { useWarehouseStock } from "@/api/warehouse-sourcing";
import { WarehouseSourcePicker } from "@/components/warehouse-source-picker";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { Link } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  FileUp,
  Loader2,
  MapPin,
  Plus,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import {
  dropshipV2ErrorMessage,
  type DropshipV2Field,
  type DropshipV2ClarificationQuestion,
  type DropshipV2Issue,
  type DropshipV2Line,
  type DropshipV2LineChanges,
  type DropshipV2LineGroup,
  type DropshipV2Plan,
  type DropshipV2Shell,
  useApproveDropshipV2Sales,
  useCreateDropshipV2Line,
  useDropshipV2AILatestBuild,
  useDropshipV2Capabilities,
  useDropshipV2Clarifications,
  useDropshipV2Fields,
  useDropshipV2DeliveryTiming,
  useDropshipV2FreightReview,
  useDropshipV2Issues,
  useDropshipV2LineGroups,
  useDropshipV2Plan,
  usePatchDropshipV2Field,
  usePatchDropshipV2Line,
  useRemoveDropshipV2Line,
  useUpdateDropshipV2Assignments,
} from "@/api/dropship-v2";
import {
  DropshipLineItemTable,
  type DropshipLineSortMode,
} from "@/components/dropship-line-item-table";
import { DropshipAIQuoteUpload } from "@/components/dropship-ai-quote-upload";
import { DropshipQuoteAgent } from "@/components/dropship-quote-agent";
import {
  DropshipFreightReview,
  DropshipManufacturerFreightReview,
} from "@/components/dropship-freight-review";
import {
  ManufacturerAssignmentPicker,
  type ManufacturerAssignment,
} from "@/components/manufacturer-assignment-picker";
import { ActionPopover } from "@/components/action-popover";
import { LoadingButton } from "@/components/loading-button";
import {
  ProductSearchPanel,
  type PickedProduct,
} from "@/components/products/product-search-panel";
import {
  POInlineEditableField,
  type POInlineEditKind,
} from "@/components/po-inline-edit";
import {
  ReviewApprovalPanel,
  type ReviewCheck,
} from "@/components/review-approval-panel";
import { ReviewChoiceGroup } from "@/components/review-choice-group";
import { useCurrentManualProducts } from "@/api/manual-products";
import { StaffAssignmentPicker } from "@/components/staff-assignment-picker";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  InlineFieldInput,
  InlineFieldTextarea,
} from "@/components/inline-field-group";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { deliveryTiming, shortDate } from "@/lib/delivery-timing";
import { cn, formatMoney, formatStatus } from "@/lib/utils";
import { flattenLines, orderedGroups } from "@/features/dropship/projections";

type ApprovalWorkflowRenderer = (args: {
  plan: DropshipV2Plan;
  canPMApprove: boolean;
  canPlace: boolean;
  fields: DropshipV2Field[];
  stage: "operations" | "project";
  onOperationsComplete: () => void;
}) => ReactNode;

function safeText(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number")
    return String(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return "";
}

function domFieldId(fieldKey: string) {
  return `dropship-field-${fieldKey.replaceAll(".", "-")}`;
}

function fieldMap(fields: DropshipV2Field[]) {
  return new Map(fields.map((field) => [field.field_key, field]));
}

function fieldText(byKey: Map<string, DropshipV2Field>, key: string) {
  return safeText(byKey.get(key)?.value);
}

function dateInputValue(value: unknown) {
  const text = safeText(value);
  return /^\d{4}-\d{2}-\d{2}/.exec(text)?.[0] ?? "";
}

function dateDisplayValue(value: unknown) {
  const date = dateInputValue(value);
  if (!date) return "";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${date}T00:00:00Z`));
}

function InlineDraftField({
  draftUuid,
  field,
  label,
  placeholder = "-",
  className,
  buttonClassName,
  kind = "text",
  hideLabel = false,
  invalid = false,
  displayValue,
}: {
  draftUuid: string;
  field?: DropshipV2Field;
  label: string;
  placeholder?: string;
  className?: string;
  buttonClassName?: string;
  kind?: POInlineEditKind;
  hideLabel?: boolean;
  invalid?: boolean;
  displayValue?: string;
}) {
  const mutation = usePatchDropshipV2Field(draftUuid);
  if (!field) return <Skeleton className={cn("h-8 w-28", className)} />;

  return (
    <div
      id={domFieldId(field.field_key)}
      tabIndex={-1}
      className={cn(
        "min-w-0 scroll-mt-24 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
        className,
      )}
    >
      {!hideLabel ? (
        <span className="block text-xs font-medium text-muted-foreground">
          {label}
        </span>
      ) : null}
      <POInlineEditableField
        value={kind === "date" ? dateInputValue(field.value) : field.value}
        displayValue={
          displayValue ??
          (kind === "date"
            ? dateDisplayValue(field.value)
            : safeText(field.value))
        }
        placeholder={placeholder}
        kind={kind}
        invalid={invalid}
        ariaLabel={`Edit ${label}`}
        pending={mutation.isPending}
        showEditIcon
        allowWrap={hideLabel}
        buttonClassName={cn(
          "min-w-28 max-w-full px-1 text-left",
          buttonClassName,
        )}
        inputClassName={kind === "textarea" ? "min-w-64" : "min-w-36"}
        onConfirm={(value) =>
          mutation.mutate({ field, value: value.trim() || null })
        }
      />
    </div>
  );
}

function DetailGroup({
  icon,
  title,
  children,
  className,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn("min-w-0 px-4 py-4", className)}>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
        {icon} {title}
      </h2>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function QuoteDetailStrip({
  draftUuid,
  fields,
}: {
  draftUuid: string;
  fields: DropshipV2Field[];
}) {
  const byKey = useMemo(() => fieldMap(fields), [fields]);
  const field = (key: string) => byKey.get(key);
  const missing = (key: string) => !fieldText(byKey, key).trim();

  return (
    <div className="overflow-hidden rounded-xl border bg-background">
      <div className="grid divide-y xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] xl:divide-x xl:divide-y-0">
        <DetailGroup
          icon={<FileUp className="size-4 text-muted-foreground" />}
          title="Quote & customer"
        >
          <div className="grid gap-x-5 gap-y-3 sm:grid-cols-3">
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("quote.job_reference_number")}
              label="Job reference"
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("customer.name")}
              label="Customer"
              invalid={missing("customer.name")}
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("customer.contact_name")}
              label="Contact"
              invalid={missing("customer.contact_name")}
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("quote.date")}
              label="Quote date"
              kind="date"
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("quote.deliver_by")}
              label="Deliver by"
              kind="date"
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("customer.email")}
              label="Email"
              invalid={missing("customer.email")}
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("customer.phone")}
              label="Phone"
              invalid={missing("customer.phone")}
            />
          </div>
        </DetailGroup>

        <DetailGroup
          icon={<MapPin className="size-4 text-muted-foreground" />}
          title="Deliver to"
        >
          <div className="grid gap-x-5 gap-y-3 sm:grid-cols-2">
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("ship_to.name")}
              label="Location"
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("ship_to.contact_name")}
              label="Delivery contact"
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("ship_to.phone")}
              label="Delivery phone"
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("ship_to.email")}
              label="Delivery email"
            />
          </div>
          <div className="grid gap-x-5 gap-y-3 sm:grid-cols-[minmax(12rem,2fr)_minmax(8rem,1fr)]">
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("ship_to.address1")}
              label="Address"
              invalid={missing("ship_to.address1")}
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("ship_to.address2")}
              label="Address 2"
            />
          </div>
          <div className="grid grid-cols-2 gap-x-5 gap-y-3 sm:grid-cols-4">
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("ship_to.city")}
              label="City"
              invalid={missing("ship_to.city")}
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("ship_to.state")}
              label="State"
              invalid={missing("ship_to.state")}
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("ship_to.zip")}
              label="ZIP"
              invalid={missing("ship_to.zip")}
            />
            <InlineDraftField
              draftUuid={draftUuid}
              field={field("ship_to.country")}
              label="Country"
              placeholder="USA"
            />
          </div>
        </DetailGroup>
      </div>

      <details className="group border-t">
        <summary className="flex min-h-11 cursor-pointer list-none items-center gap-1.5 px-4 py-2 text-sm font-medium hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/50">
          <span>Additional quote, billing, and USA-RS contact details</span>
          <ChevronDown
            className="size-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>
        <div className="grid gap-x-6 gap-y-4 border-t bg-muted/10 px-4 py-4 sm:grid-cols-2 xl:grid-cols-3">
          {(
            [
              ["quote.memo", "Quote notes", "textarea"],
              ["bill_to.name", "Bill-to company", "text"],
              ["bill_to.contact_name", "Billing contact", "text"],
              ["bill_to.email", "Billing email", "text"],
              ["bill_to.phone", "Billing phone", "text"],
              ["bill_to.address1", "Billing address", "text"],
              ["bill_to.city", "Billing city", "text"],
              ["bill_to.state", "Billing state", "text"],
              ["bill_to.zip", "Billing ZIP", "text"],
              ["bill_to.country", "Billing country", "text"],
              ["from.contact_name", "USA-RS salesperson", "text"],
              ["from.email", "USA-RS email", "text"],
              ["from.phone", "USA-RS phone", "text"],
            ] as const
          ).map(([key, label, kind]) => (
            <InlineDraftField
              key={key}
              draftUuid={draftUuid}
              field={field(key)}
              label={label}
              kind={kind}
            />
          ))}
        </div>
      </details>
    </div>
  );
}

function ReviewOwners({
  draftUuid,
  shell,
}: {
  draftUuid: string;
  shell: DropshipV2Shell;
}) {
  const update = useUpdateDropshipV2Assignments(draftUuid);
  return (
    <div
      id="dropship-review-owners"
      tabIndex={-1}
      className="flex scroll-mt-24 flex-wrap items-center gap-2 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
    >
      <span className="mr-1 text-xs font-medium text-muted-foreground">
        Review owners
      </span>
      <StaffAssignmentPicker
        label="Sales"
        currentUserId={shell.assigned_sales_user_id}
        pending={update.isPending}
        onChange={(user) =>
          update.mutate({
            shell,
            assignedSalesUserId: user?.id ?? null,
            assignedPMUserId: shell.assigned_pm_user_id ?? null,
          })
        }
      />
      <StaffAssignmentPicker
        label="Operations"
        currentUserId={shell.assigned_pm_user_id}
        pending={update.isPending}
        onChange={(user) =>
          update.mutate({
            shell,
            assignedSalesUserId: shell.assigned_sales_user_id ?? null,
            assignedPMUserId: user?.id ?? null,
          })
        }
      />
    </div>
  );
}

function LineCreateDialog({
  draftUuid,
  shell,
  open,
  parent,
  onOpenChange,
}: {
  draftUuid: string;
  shell: DropshipV2Shell;
  open: boolean;
  parent: DropshipV2Line | null;
  onOpenChange: (open: boolean) => void;
}) {
  const [lineKind, setLineKind] = useState<
    "configuration" | "accessory" | "warranty" | "informational"
  >("configuration");
  const [manufacturer, setManufacturer] =
    useState<ManufacturerAssignment | null>(null);
  const [model, setModel] = useState("");
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [netPrice, setNetPrice] = useState("0");
  const [sellPrice, setSellPrice] = useState("0");
  const [sourcePO, setSourcePO] = useState("");
  const [pendingProduct, setPendingProduct] =
    useState<PickedProduct | null>(null);
  // A product priced on each quote waits here for its Net and Sell.
  const [pricingProduct, setPricingProduct] =
    useState<PickedProduct | null>(null);
  const [customMode, setCustomMode] = useState(false);
  const [productPage, setProductPage] = useState(1);
  // A warranty sub-line can be a plan we define (Safeware), bought on its own PO (USA-1108).
  const [planUuid, setPlanUuid] = useState("custom");
  const plans = useCurrentManualProducts(open && Boolean(parent)).data ?? [];
  const showPlans = Boolean(parent) && lineKind === "warranty" && plans.length > 0;
  const plan = showPlans
    ? plans.find((candidate) => candidate.uuid === planUuid)
    : undefined;
  const mutation = useCreateDropshipV2Line(draftUuid);

  const reset = () => {
    setLineKind("configuration");
    setManufacturer(null);
    setModel("");
    setDescription("");
    setQuantity("1");
    setNetPrice("0");
    setSellPrice("0");
    setSourcePO("");
    setPendingProduct(null);
    setPricingProduct(null);
    setCustomMode(false);
    setProductPage(1);
    setPlanUuid("custom");
  };
  const close = () => {
    onOpenChange(false);
    reset();
  };
  const canSubmit = plan
    ? netPrice.trim() !== "" &&
      Number(netPrice) >= 0 &&
      sellPrice.trim() !== "" &&
      Number(sellPrice) > 0
    : Boolean(model.trim() && (parent || manufacturer));

  const submit = () => {
    if (!canSubmit) return;
    if (plan && parent) {
      mutation.mutate(
        {
          expected_content_revision: shell.content_revision,
          parent_line_uuid: parent.uuid,
          line_kind: "warranty",
          product_uuid: plan.uuid,
          quantity,
          net_price: netPrice,
          sell_price: sellPrice,
          source_purchase_order_id: sourcePO.trim() || undefined,
        },
        {
          onSuccess: () => {
            toast.success(`${plan.model} added to ${parent.display_number}`);
            close();
          },
          onError: (error) =>
            toast.error(
              dropshipV2ErrorMessage(error, "Warranty could not be added"),
            ),
        },
      );
      return;
    }
    mutation.mutate(
      {
        expected_content_revision: shell.content_revision,
        parent_line_uuid: parent?.uuid,
        line_kind: parent ? lineKind : "main",
        manufacturer_id: parent ? undefined : manufacturer?.id,
        model: model.trim(),
        description: description.trim(),
        quantity,
        net_price: netPrice,
        sell_price: sellPrice,
        source_purchase_order_id: sourcePO.trim() || undefined,
      },
      {
        onSuccess: () => {
          toast.success(
            parent
              ? "Sub-line added with its equipment"
              : "Equipment line added",
          );
          close();
        },
        onError: (error) =>
          toast.error(dropshipV2ErrorMessage(error, "Line could not be added")),
      },
    );
  };
  const canSubmitPrice =
    netPrice.trim() !== "" &&
    Number(netPrice) >= 0 &&
    sellPrice.trim() !== "" &&
    Number(sellPrice) > 0;
  const chooseProduct = (product: PickedProduct) => {
    if (mutation.isPending) return;
    if (product.pricedOnEachQuote) {
      setNetPrice("");
      setSellPrice("");
      setPricingProduct(product);
      return;
    }
    addProduct(product);
  };
  const addProduct = (
    product: PickedProduct,
    prices?: { net_price: string; sell_price: string },
  ) => {
    setPendingProduct(product);
    mutation.mutate(
      {
        expected_content_revision: shell.content_revision,
        line_kind: "main",
        product_uuid: product.uuid,
        quantity: "1",
        // A Quick Ship card: the line comes from our warehouse in its condition (USA-1574).
        ...(product.warehouseSource ? { warehouse_source: product.warehouseSource } : {}),
        ...prices,
      },
      {
        onSuccess: () => {
          toast.success(
            prices
              ? `${product.model} added`
              : `${product.model} added with current catalog pricing`,
          );
          close();
        },
        onError: (error) => {
          setPendingProduct(null);
          toast.error(
            dropshipV2ErrorMessage(error, "Catalog product could not be added"),
          );
        },
      },
    );
  };
  const showCatalogSearch = !parent && !customMode && !pricingProduct;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && mutation.isPending) return;
        if (!nextOpen) reset();
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent
        className={cn(
          "max-h-[90vh] overflow-y-auto",
          showCatalogSearch ? "sm:max-w-6xl" : "sm:max-w-2xl",
        )}
        showCloseButton={!mutation.isPending}
        onEscapeKeyDown={(event) =>
          mutation.isPending && event.preventDefault()
        }
        onPointerDownOutside={(event) =>
          mutation.isPending && event.preventDefault()
        }
      >
        <DialogHeader className="space-y-0 pb-1">
          <DialogTitle className="text-base">
            {parent
              ? `Add sub-line to ${parent.display_number}`
              : "Add equipment"}
          </DialogTitle>
        </DialogHeader>
        {showCatalogSearch ? (
          <div className="space-y-4">
            {pendingProduct ? (
              <div
                role="status"
                aria-live="polite"
                className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm"
              >
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                <span className="min-w-0 truncate">
                  Adding {pendingProduct.model} with current catalog pricing...
                </span>
              </div>
            ) : null}
            <ProductSearchPanel
              page={productPage}
              onPageChange={setProductPage}
              onSelectProduct={chooseProduct}
              selectionDisabled={mutation.isPending}
              defaultResults={<div className="min-h-20" aria-hidden="true" />}
              showHeader={false}
            />
            <div className="flex justify-end border-t pt-4">
              <Button
                type="button"
                variant="outline"
                disabled={mutation.isPending}
                onClick={() => setCustomMode(true)}
              >
                Add manual item
              </Button>
            </div>
          </div>
        ) : pricingProduct ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-md bg-muted/50 px-3 py-3 text-sm sm:col-span-2">
              <span className="font-medium">
                {pricingProduct.model} · {pricingProduct.manufacturer}
              </span>
              <span className="mt-1 block text-muted-foreground">
                {pricingProduct.title}
              </span>
              {pricingProduct.boughtThrough ? (
                <span className="mt-1 block text-muted-foreground">
                  Bought through {pricingProduct.boughtThrough}
                </span>
              ) : null}
            </div>
            <div className="space-y-2">
              <Label htmlFor="priced-line-net">Supplier net</Label>
              <InlineFieldInput
                id="priced-line-net"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={netPrice}
                onChange={(event) => setNetPrice(event.target.value)}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="priced-line-sell">Customer sell</Label>
              <InlineFieldInput
                id="priced-line-sell"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={sellPrice}
                onChange={(event) => setSellPrice(event.target.value)}
              />
            </div>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {parent ? (
              <div className="rounded-md bg-muted/50 px-3 py-3 text-sm sm:col-span-2">
                <span className="font-medium">
                  {parent.display_number} · {parent.model}
                </span>
                <span className="mt-1 block text-muted-foreground">
                  {parent.manufacturer_name}
                </span>
              </div>
            ) : (
              <div className="space-y-2 sm:col-span-2">
                <Label>Manufacturer</Label>
                <ManufacturerAssignmentPicker
                  currentId={manufacturer?.id}
                  currentName={manufacturer?.name}
                  allowCreate
                  onChange={setManufacturer}
                  className="w-full justify-start border"
                />
              </div>
            )}
            {parent ? (
              <div className="space-y-2 sm:col-span-2">
                <Label>Sub-line type</Label>
                <ReviewChoiceGroup
                  label="New sub-line type"
                  value={lineKind}
                  options={[
                    { value: "configuration", label: "Configuration" },
                    { value: "accessory", label: "Accessory" },
                    { value: "warranty", label: "Warranty" },
                    { value: "informational", label: "Informational" },
                  ]}
                  onChange={setLineKind}
                />
              </div>
            ) : null}
            {showPlans ? (
              <div className="space-y-2 sm:col-span-2">
                <Label>Warranty plan</Label>
                <ReviewChoiceGroup
                  label="Warranty plan"
                  value={planUuid}
                  options={[
                    ...plans.map((candidate) => ({
                      value: candidate.uuid,
                      label: candidate.store_title || candidate.model,
                    })),
                    { value: "custom", label: "Custom" },
                  ]}
                  onChange={(value) => {
                    setPlanUuid(value);
                    if (value !== "custom") {
                      setNetPrice("");
                      setSellPrice("");
                    }
                  }}
                />
                {plan?.specifications ? (
                  <p className="text-sm text-muted-foreground">
                    {plan.specifications}
                  </p>
                ) : null}
              </div>
            ) : null}
            {plan ? null : (
              <div className="space-y-2">
                <Label htmlFor="new-line-model">Model or item label</Label>
                <InlineFieldInput
                  id="new-line-model"
                  value={model}
                  onChange={(event) => setModel(event.target.value)}
                  autoFocus
                />
              </div>
            )}
            <div className={cn("space-y-2", plan && "sm:col-span-2")}>
              <Label htmlFor="new-line-quantity">Quantity</Label>
              <InlineFieldInput
                id="new-line-quantity"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
              />
            </div>
            {plan ? null : (
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="new-line-description">Description</Label>
                <InlineFieldTextarea
                  id="new-line-description"
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="new-line-net">Supplier net</Label>
              <InlineFieldInput
                id="new-line-net"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={netPrice}
                onChange={(event) => setNetPrice(event.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-line-sell">Customer sell</Label>
              <InlineFieldInput
                id="new-line-sell"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                value={sellPrice}
                onChange={(event) => setSellPrice(event.target.value)}
              />
            </div>
            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="new-line-source-po">
                AQ purchase order / release reference (optional)
              </Label>
              <InlineFieldInput
                id="new-line-source-po"
                value={sourcePO}
                onChange={(event) => setSourcePO(event.target.value)}
              />
            </div>
          </div>
        )}
        {pricingProduct ? (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => setPricingProduct(null)}
            >
              <ArrowLeft className="size-4" />
              Back
            </Button>
            <LoadingButton
              type="button"
              disabled={!canSubmitPrice || mutation.isPending}
              loading={mutation.isPending}
              loadingText="Adding equipment..."
              onClick={() =>
                addProduct(pricingProduct, {
                  net_price: netPrice,
                  sell_price: sellPrice,
                })
              }
            >
              <Plus className="size-4" />
              Add equipment
            </LoadingButton>
          </DialogFooter>
        ) : !showCatalogSearch ? (
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Cancel
            </Button>
            <LoadingButton
              type="button"
              disabled={!canSubmit || mutation.isPending}
              loading={mutation.isPending}
              loadingText={
                parent ? "Adding sub-line..." : "Adding equipment..."
              }
              onClick={submit}
            >
              <Plus className="size-4" />
              Add {parent ? "sub-line" : "equipment"}
            </LoadingButton>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

type DraftReviewCategory =
  | "data"
  | "customer"
  | "catalog"
  | "configuration"
  | "pricing"
  | "freight"
  | "delivery"
  | "clarifications"
  | "advisories";

type ReviewFinding = {
  id: string;
  detail: string;
  blocking: boolean;
  onClick?: () => void;
  actionLabel?: string;
};

type FreightReviewBlocker = {
  id: string;
  detail: string;
  code: string;
  groupingKey: string | null;
};

const CATEGORY_META: Record<DraftReviewCategory, { label: string }> = {
  data: { label: "Data readiness" },
  customer: { label: "Customer and delivery" },
  catalog: { label: "Catalog and manufacturer" },
  configuration: { label: "Configurations and accessories" },
  pricing: { label: "Pricing" },
  freight: { label: "Freight" },
  delivery: { label: "Delivery timing" },
  clarifications: { label: "Clarifications" },
  advisories: { label: "Assignments and advisories" },
};

function categoryForIssue(issue: DropshipV2Issue): DraftReviewCategory {
  const searchable = `${issue.code} ${issue.field_key ?? ""}`.toLowerCase();
  if (
    issue.code === "ai_clarification_required" ||
    searchable.includes("clarif")
  )
    return "clarifications";
  if (searchable.includes("freight")) return "freight";
  if (
    issue.field_key?.startsWith("customer.") ||
    issue.field_key?.startsWith("bill_to.") ||
    issue.field_key?.startsWith("ship_to.") ||
    ["customer", "contact", "delivery", "address"].some((word) =>
      searchable.includes(word),
    )
  )
    return "customer";
  if (
    [
      "manufacturer",
      "catalog",
      "product",
      "model",
      "sku",
      "custom_estimate",
    ].some((word) => searchable.includes(word))
  )
    return "catalog";
  if (
    [
      "configuration",
      "accessory",
      "accessorial",
      "disposition",
      "exclusion",
    ].some((word) => searchable.includes(word))
  )
    return "configuration";
  if (
    [
      "price",
      "pricing",
      "total",
      "quantity",
      "discount",
      "margin",
      "net",
      "sell",
      "list",
    ].some((word) => searchable.includes(word))
  )
    return "pricing";
  return "advisories";
}

function categoryCheck(
  category: DraftReviewCategory,
  findings: ReviewFinding[],
): ReviewCheck {
  const uniqueFindings = [
    ...new Map(findings.map((finding) => [finding.id, finding])).values(),
  ];
  const blockingCount = uniqueFindings.filter(
    (finding) => finding.blocking,
  ).length;
  const firstAction = uniqueFindings.find((finding) => finding.onClick);
  const meta = CATEGORY_META[category];
  const detail =
    uniqueFindings.length === 0
      ? undefined
      : uniqueFindings.length === 1
        ? uniqueFindings[0]?.detail
        : blockingCount === uniqueFindings.length
          ? `${blockingCount} blockers need review`
          : blockingCount
            ? `${blockingCount} blocker${blockingCount === 1 ? "" : "s"} and ${uniqueFindings.length - blockingCount} advisor${uniqueFindings.length - blockingCount === 1 ? "y" : "ies"}`
            : `${uniqueFindings.length} advisor${uniqueFindings.length === 1 ? "y" : "ies"} to review`;
  return {
    id: category,
    label: meta.label,
    detail,
    count: uniqueFindings.length,
    state: blockingCount
      ? "blocking"
      : uniqueFindings.length
        ? "warning"
        : "complete",
    onClick: firstAction?.onClick,
    actionLabel: firstAction?.actionLabel,
  };
}

function checksForDraft({
  byKey,
  groups,
  issues,
  clarifications,
  dataChecks,
  freightBlockers,
  lateDelivery,
  shell,
  onSelectIssue,
  onSelectClarification,
  onSelectFreight,
  onSelectOwners,
}: {
  byKey: Map<string, DropshipV2Field>;
  groups: DropshipV2LineGroup[];
  issues: DropshipV2Issue[];
  clarifications: DropshipV2ClarificationQuestion[];
  dataChecks: ReviewCheck[];
  freightBlockers: FreightReviewBlocker[];
  lateDelivery: string | null;
  shell: DropshipV2Shell;
  onSelectIssue: (issue: DropshipV2Issue) => void;
  onSelectClarification: (question: DropshipV2ClarificationQuestion) => void;
  onSelectFreight: () => void;
  onSelectOwners: () => void;
}): { checks: ReviewCheck[]; blockingCount: number } {
  const findings = new Map<DraftReviewCategory, ReviewFinding[]>(
    (Object.keys(CATEGORY_META) as DraftReviewCategory[]).map((category) => [
      category,
      [],
    ]),
  );
  const add = (category: DraftReviewCategory, finding: ReviewFinding) => {
    findings.get(category)?.push(finding);
  };
  for (const check of dataChecks) {
    add("data", {
      id: `data:${check.id}`,
      detail: check.label,
      blocking: check.state === "blocking" || check.state === "pending",
      onClick: check.onClick,
      actionLabel: check.onClick ? "Retry verification" : undefined,
    });
  }

  const activeClarifications = clarifications.filter(
    (question) =>
      question.status === "open" &&
      !["resolved", "stale"].includes(question.workflow_state),
  );
  const linkedClarificationIssueIds = new Set(
    clarifications.map((question) => question.issue_id),
  );
  for (const question of activeClarifications) {
    add("clarifications", {
      id: `issue:${question.issue_id}`,
      detail: question.question,
      blocking: question.blocking,
      onClick: () => onSelectClarification(question),
      actionLabel: "Open in quote agent",
    });
  }

  for (const issue of issues.filter((item) => item.status === "open")) {
    if (
      categoryForIssue(issue) === "clarifications" &&
      linkedClarificationIssueIds.has(issue.id)
    )
      continue;
    add(categoryForIssue(issue), {
      id: `issue:${issue.id}`,
      detail: issue.message,
      blocking: issue.blocking,
      onClick: () => onSelectIssue(issue),
      actionLabel: issue.blocking ? "Review blocker" : "Review advisory",
    });
  }

  const requiredFields = [
    "customer.name",
    "customer.contact_name",
    "customer.email",
    "customer.phone",
    "ship_to.address1",
    "ship_to.city",
    "ship_to.state",
    "ship_to.zip",
  ];
  for (const key of requiredFields.filter(
    (fieldKey) => !fieldText(byKey, fieldKey),
  )) {
    if (
      issues.some((issue) => issue.status === "open" && issue.field_key === key)
    )
      continue;
    add("customer", {
      id: `field:${key}`,
      detail: `${formatStatus(key.split(".").at(-1) ?? key)} is missing`,
      blocking: true,
      onClick: () => document.getElementById(domFieldId(key))?.focus(),
      actionLabel: "Complete field",
    });
  }

  for (const group of groups.filter(
    (item) =>
      !item.root.manufacturer_id &&
      !["internal_only", "excluded_with_reason", "removed"].includes(
        item.root.disposition,
      ),
  )) {
    if (
      issues.some(
        (issue) =>
          issue.status === "open" &&
          issue.line_id === group.root.id &&
          issue.code.toLowerCase().includes("manufacturer"),
      )
    )
      continue;
    add("catalog", {
      id: `line:${group.root.id}:manufacturer`,
      detail: `Line ${group.root.display_number} needs a canonical manufacturer`,
      blocking: true,
      onClick: () => {
        const target = document.getElementById(
          `dropship-line-${group.root.id}`,
        );
        target?.scrollIntoView({ behavior: "smooth", block: "center" });
        target?.focus();
      },
      actionLabel: "Review manufacturer",
    });
  }

  const normalizedFreightCode = (code: string) =>
    code.replace(/^freight_(?:quote|pricing)_/, "").toLowerCase();
  const freightIssues = issues.filter(
    (issue) => issue.status === "open" && categoryForIssue(issue) === "freight",
  );
  for (const blocker of freightBlockers) {
    const hasCanonicalIssue =
      blocker.groupingKey != null &&
      freightIssues.some((issue) => {
        const details = issue.details as Record<string, unknown>;
        return (
          details.grouping_key === blocker.groupingKey &&
          normalizedFreightCode(issue.code) ===
            normalizedFreightCode(blocker.code)
        );
      });
    if (hasCanonicalIssue) continue;
    add("freight", {
      id: `freight:${blocker.id}`,
      detail: blocker.detail,
      blocking: true,
      onClick: onSelectFreight,
      actionLabel: "Review manufacturer freight",
    });
  }
  const freightFinding = findings
    .get("freight")
    ?.find((finding) => finding.blocking);
  if (freightFinding) {
    freightFinding.onClick = onSelectFreight;
    freightFinding.actionLabel = "Review manufacturer freight";
  }

  if (lateDelivery) {
    add("delivery", {
      id: "delivery:late",
      detail: lateDelivery,
      blocking: false,
      onClick: () =>
        document.getElementById(domFieldId("quote.deliver_by"))?.focus(),
      actionLabel: "Review deliver-by date",
    });
  }

  const missingOwners = [
    !shell.assigned_sales_user_id ? "Sales owner is unassigned" : null,
    !shell.assigned_pm_user_id ? "Operations owner is unassigned" : null,
  ].filter((value): value is string => value != null);
  for (const [index, detail] of missingOwners.entries()) {
    add("advisories", {
      id: `owner:${index}`,
      detail,
      blocking: false,
      onClick: onSelectOwners,
      actionLabel: "Review assignments",
    });
  }

  const checks = (Object.keys(CATEGORY_META) as DraftReviewCategory[])
    // Delivery timing only means something once the customer's deliver-by date is set.
    .filter(
      (category) =>
        category !== "delivery" || Boolean(fieldText(byKey, "quote.deliver_by")),
    )
    .map((category) => categoryCheck(category, findings.get(category) ?? []));
  const blockerIds = new Set(
    [...findings.values()]
      .flat()
      .filter((finding) => finding.blocking)
      .map((finding) => finding.id),
  );
  return { checks, blockingCount: blockerIds.size };
}

export function DropshipDraftReviewPage({
  draftUuid,
  shell,
  renderProcessing,
  renderPlanWorkflow,
}: {
  draftUuid: string;
  shell: DropshipV2Shell;
  renderProcessing: () => ReactNode;
  renderPlanWorkflow: ApprovalWorkflowRenderer;
}) {
  const ready = shell.processing_stage === "ready";
  const activeSalesChangeRequest = shell.active_sales_change_request;
  const [trackedAIBuildUuid, setTrackedAIBuildUuid] = useState<string | null>(
    null,
  );
  const capabilities = useDropshipV2Capabilities();
  const aiBuild = useDropshipV2AILatestBuild(
    draftUuid,
    shell.source_kind === "ai_document" || trackedAIBuildUuid != null,
  );
  const draftDataEnabled = ready || Boolean(aiBuild.data);
  const fields = useDropshipV2Fields(draftUuid, draftDataEnabled);
  const issues = useDropshipV2Issues(draftUuid, draftDataEnabled);
  const clarifications = useDropshipV2Clarifications(
    draftUuid,
    draftDataEnabled,
  );
  const freightReview = useDropshipV2FreightReview(draftUuid, draftDataEnabled);
  const lineQuery = useDropshipV2LineGroups(draftUuid, draftDataEnabled);
  const planQuery = useDropshipV2Plan(
    draftUuid,
    activeSalesChangeRequest ? null : shell.current_po_plan_id,
    ready && !activeSalesChangeRequest && shell.current_po_plan_id != null,
  );
  const currentPlan =
    !activeSalesChangeRequest && planQuery.data?.id === shell.current_po_plan_id
      ? planQuery.data
      : undefined;
  const linePatch = usePatchDropshipV2Line(draftUuid);
  const lineRemoval = useRemoveDropshipV2Line(draftUuid);
  const salesApproval = useApproveDropshipV2Sales(draftUuid);
  const {
    fetchNextPage: fetchNextLinePage,
    hasNextPage: hasNextLinePage,
    isFetchNextPageError,
    isFetchingNextPage: isFetchingNextLinePage,
  } = lineQuery;
  const [sortMode, setSortMode] = useState<DropshipLineSortMode>("quote");
  const [revealedLineId, setRevealedLineId] = useState<number>();
  const [revealNonce, setRevealNonce] = useState(0);
  const [revealedManufacturerKey, setRevealedManufacturerKey] =
    useState<string>();
  const [lineDialogOpen, setLineDialogOpen] = useState(false);
  const [lineDialogParent, setLineDialogParent] =
    useState<DropshipV2Line | null>(null);
  const [salesNote, setSalesNote] = useState("");
  const [minimumMarginOverrideOpen, setMinimumMarginOverrideOpen] =
    useState(false);
  const [reviewView, setReviewView] = useState<
    "auto" | "sales" | "operations" | "project"
  >("auto");
  const [quoteAgentOpen, setQuoteAgentOpen] = useState(false);
  const [quoteAgentExplicitlyOpened, setQuoteAgentExplicitlyOpened] =
    useState(false);
  const [focusedClarificationUuid, setFocusedClarificationUuid] = useState<
    string | null
  >(null);
  const [selectedMatchLineUuid, setSelectedMatchLineUuid] = useState<
    string | null
  >(null);
  const loadedLinePageCount = lineQuery.data?.pages.length ?? 0;

  useEffect(() => {
    if (!hasNextLinePage || isFetchNextPageError || isFetchingNextLinePage)
      return;
    void fetchNextLinePage();
  }, [
    fetchNextLinePage,
    hasNextLinePage,
    isFetchNextPageError,
    isFetchingNextLinePage,
    loadedLinePageCount,
  ]);

  const groups = useMemo(
    () => lineQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [lineQuery.data],
  );
  // What our warehouse holds of the loaded lines' products (USA-1574).
  const warehouseStock = useWarehouseStock(
    groups.flatMap((group) =>
      group.root.product_id != null ? [group.root.product_id] : [],
    ),
  );
  const pendingLineUuids = useMemo(() => {
    if (!linePatch.isPending) return undefined;
    return new Set(flattenLines(groups).map((line) => line.uuid));
  }, [groups, linePatch.isPending]);
  const lineDataLoading = Boolean(
    lineQuery.isLoading || lineQuery.isFetchingNextPage,
  );
  const selectedMatchLine = useMemo(
    () =>
      selectedMatchLineUuid
        ? (flattenLines(groups).find(
            (line) => line.uuid === selectedMatchLineUuid,
          ) ?? null)
        : null,
    [groups, selectedMatchLineUuid],
  );
  const displayedGroups = useMemo(
    () => orderedGroups(groups, sortMode),
    [groups, sortMode],
  );
  const showRetainedEquipment = Boolean(
    shell.source_kind === "ai_document" &&
    shell.processing_stage === "failed" &&
    aiBuild.data?.terminal &&
    groups.length > 0,
  );
  const byKey = useMemo(() => fieldMap(fields.data ?? []), [fields.data]);
  const openIssues = useMemo(() => issues.data ?? [], [issues.data]);
  const minimumMarginIssues = useMemo(
    () =>
      openIssues.filter(
        (issue) =>
          issue.status === "open" &&
          issue.blocking &&
          issue.code === "minimum_margin_violation",
      ),
    [openIssues],
  );
  const lineBlockers = useMemo(() => {
    const byLine = new Map<number, DropshipV2Issue[]>();
    for (const issue of openIssues) {
      if (issue.status !== "open" || !issue.blocking || issue.line_id == null)
        continue;
      byLine.set(issue.line_id, [...(byLine.get(issue.line_id) ?? []), issue]);
    }
    return byLine;
  }, [openIssues]);
  const clarificationQuestions = useMemo(
    () => clarifications.data?.questions ?? [],
    [clarifications.data],
  );
  const reviewableMatchLineUuids = useMemo(
    () =>
      new Set(
        (aiBuild.data?.line_agents ?? [])
          .filter(
            (agent) =>
              Boolean(agent.selected_match) || agent.alternatives.length > 0,
          )
          .map((agent) => agent.line_uuid),
      ),
    [aiBuild.data],
  );

  // The line table reveals the line once its group is loaded; every page is fetched eagerly.
  const selectIssue = useCallback((issue: DropshipV2Issue) => {
    if (issue.field_key?.includes(".") && !issue.line_id) {
      const target = document.getElementById(domFieldId(issue.field_key));
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
      target?.focus();
      return;
    }
    if (!issue.line_id) return;
    setRevealedLineId(issue.line_id);
    setRevealNonce((current) => current + 1);
  }, []);

  const selectClarification = useCallback(
    (question: DropshipV2ClarificationQuestion) => {
      setQuoteAgentExplicitlyOpened(true);
      setQuoteAgentOpen(true);
      setFocusedClarificationUuid(question.uuid);
    },
    [],
  );

  const selectFreightManufacturer = useCallback(() => {
    const reviewGroups = freightReview.data?.groups ?? [];
    const blockedGroup =
      reviewGroups.find((group) => group.blocking) ?? reviewGroups[0];
    const matchingRoot = groups.find((group) =>
      blockedGroup?.root_lines?.some((line) => line.uuid === group.root.uuid),
    )?.root;
    const manufacturerId =
      blockedGroup?.manufacturer_id ?? matchingRoot?.manufacturer_id;
    const manufacturerName =
      blockedGroup?.manufacturer_name ?? matchingRoot?.manufacturer_name;
    const manufacturerKey = manufacturerId
      ? `id:${manufacturerId}`
      : `name:${manufacturerName || "needs-review"}`;
    setSortMode("manufacturer");
    setRevealedManufacturerKey(manufacturerKey);
  }, [freightReview.data, groups]);

  const selectOwners = useCallback(() => {
    const target = document.getElementById("dropship-review-owners");
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
    target?.focus();
  }, []);

  const reviewDataChecks = useMemo<ReviewCheck[]>(() => {
    const dataChecks: ReviewCheck[] = [];
    const lineCountMismatch = groups.length !== shell.root_line_count;
    const lineError = Boolean(
      lineQuery.error && (!lineQuery.data || lineCountMismatch),
    );

    if (lineError || lineDataLoading || lineCountMismatch) {
      dataChecks.push({
        id: "equipment-data",
        label: lineError
          ? "Equipment data could not be verified"
          : "Equipment data is still loading",
        detail: `${groups.length} of ${shell.root_line_count} equipment groups loaded. ${lineError ? "Retry before approval." : "Approval stays locked until every group is available."}`,
        state: "blocking",
        onClick: lineError
          ? () => {
              if (lineQuery.data && lineQuery.hasNextPage) {
                void lineQuery.fetchNextPage();
              } else {
                void lineQuery.refetch();
              }
            }
          : undefined,
      });
    }

    if (
      !issues.data &&
      (issues.error || issues.isLoading || issues.isFetching)
    ) {
      const issueError = Boolean(issues.error);
      dataChecks.push({
        id: "issue-data",
        label: issueError
          ? "Validation issues could not be verified"
          : "Validation issues are still loading",
        detail: issueError
          ? "Retry the validation check before approval."
          : "Approval stays locked until validation finishes.",
        state: "blocking",
        onClick: issueError ? () => void issues.refetch() : undefined,
      });
    }

    if (
      !clarifications.data &&
      (clarifications.error ||
        clarifications.isLoading ||
        clarifications.isFetching)
    ) {
      const clarificationError = Boolean(clarifications.error);
      dataChecks.push({
        id: "clarification-data",
        label: clarificationError
          ? "Quote clarifications could not be verified"
          : "Quote clarifications are still loading",
        detail: clarificationError
          ? "Retry clarification review before approval."
          : "Approval stays locked until quote questions are available.",
        state: "blocking",
        onClick: clarificationError
          ? () => void clarifications.refetch()
          : undefined,
      });
    }

    if (
      !activeSalesChangeRequest &&
      shell.current_po_plan_id != null &&
      !currentPlan
    ) {
      const planError = Boolean(planQuery.error);
      const planLoading =
        !planError && (planQuery.isLoading || planQuery.isFetching);
      dataChecks.push({
        id: "purchase-order-plan-data",
        label: planError
          ? "Purchase order plan could not be loaded"
          : planLoading
            ? "Purchase order plan is still loading"
            : "Purchase order plan could not be verified",
        detail: planError
          ? "This draft already references a purchase order plan. Retry before continuing review."
          : planLoading
            ? "Sales approval stays locked until the existing purchase order plan is available."
            : "This draft references a purchase order plan that was not returned. Retry before continuing review.",
        state: "blocking",
        onClick: planLoading ? undefined : () => void planQuery.refetch(),
      });
    }

    if (freightReview.error && !freightReview.data) {
      dataChecks.push({
        id: "freight-review-data",
        label: "Freight pricing could not be verified",
        detail: "Retry the freight review before Sales approval.",
        state: "blocking",
        onClick: () => void freightReview.refetch(),
      });
    } else if (!freightReview.data) {
      dataChecks.push({
        id: "freight-review-data",
        label: "Freight pricing is still loading",
        detail:
          "Sales approval stays locked until commercial freight is verified.",
        state: "blocking",
      });
    } else if (freightReview.data.content_revision !== shell.content_revision) {
      dataChecks.push({
        id: "freight-review-data",
        label: "Freight pricing is updating",
        detail: `Pricing revision ${freightReview.data.content_revision} is being refreshed for draft revision ${shell.content_revision}.`,
        state: "blocking",
      });
    }

    return dataChecks;
  }, [
    groups.length,
    activeSalesChangeRequest,
    clarifications,
    currentPlan,
    freightReview,
    issues,
    lineDataLoading,
    lineQuery,
    planQuery,
    shell.content_revision,
    shell.current_po_plan_id,
    shell.root_line_count,
  ]);
  const freightBlockers = useMemo(() => {
    if (!freightReview.data || freightReview.data.ready) return [];
    const pricingBlockers = freightReview.data.pricing.blockers ?? [];
    if (pricingBlockers.length) {
      return pricingBlockers.map((blocker) => ({
        id: `${blocker.grouping_key ?? "unmapped"}:${blocker.code}:${blocker.message}`,
        detail: blocker.message,
        code: blocker.code,
        groupingKey: blocker.grouping_key ?? null,
      }));
    }
    return (freightReview.data.groups ?? [])
      .filter((group) => group.blocking)
      .map((group) => ({
        id: group.grouping_key,
        detail:
          group.blocker_message ||
          `${group.manufacturer_name} freight needs attention`,
        code: group.blocker_code || group.state,
        groupingKey: group.grouping_key || null,
      }));
  }, [freightReview.data]);
  // Manufacturers arriving after the customer's deliver-by date if ordered today (USA-1329).
  const deliverBy = fieldText(byKey, "quote.deliver_by");
  const timingQuery = useDropshipV2DeliveryTiming(draftUuid, Boolean(deliverBy));
  const lateDelivery = useMemo(() => {
    const late = (timingQuery.data?.arrivals ?? [])
      .map((arrival) => ({
        arrival,
        timing: deliveryTiming(deliverBy, arrival.estimated_delivery_date),
      }))
      .filter(({ timing }) => timing?.late)
      .sort((a, b) =>
        a.arrival.estimated_delivery_date.localeCompare(
          b.arrival.estimated_delivery_date,
        ),
      );
    const last = late.at(-1);
    if (!last?.timing) return null;
    if (late.length === 1) {
      return `${last.arrival.manufacturer_name} ${last.timing.text.replace(/^Arrives/, "arrives")}`;
    }
    const names = late.map(({ arrival }) => arrival.manufacturer_name);
    return `${names.slice(0, -1).join(", ")} and ${names.at(-1)} arrive after the ${shortDate(deliverBy)} deliver-by date, the last about ${shortDate(last.arrival.estimated_delivery_date)}`;
  }, [deliverBy, timingQuery.data]);
  const draftReview = useMemo(
    () =>
      checksForDraft({
        byKey,
        groups,
        issues: openIssues,
        clarifications: clarificationQuestions,
        dataChecks: reviewDataChecks,
        freightBlockers,
        lateDelivery,
        shell,
        onSelectIssue: selectIssue,
        onSelectClarification: selectClarification,
        onSelectFreight: selectFreightManufacturer,
        onSelectOwners: selectOwners,
      }),
    [
      byKey,
      clarificationQuestions,
      freightBlockers,
      groups,
      lateDelivery,
      openIssues,
      reviewDataChecks,
      selectClarification,
      selectFreightManufacturer,
      selectIssue,
      selectOwners,
      shell,
    ],
  );
  const checks = draftReview.checks;
  const sourceTitle =
    shell.source_kind === "scratch"
      ? "Untitled dropship project"
      : shell.source_file_name.replace(/\.aqproj$/i, "").trim() ||
        "Untitled dropship project";
  const quoteTitle = fieldText(byKey, "quote.project_name") || sourceTitle;
  const plan = activeSalesChangeRequest ? undefined : currentPlan;
  const planExpected =
    !activeSalesChangeRequest && shell.current_po_plan_id != null;
  const approvalsEnabled = capabilities.data?.approvals_enabled === true;
  const canSalesApprove = approvalsEnabled;
  const canPMApprove = approvalsEnabled;
  const canPlace = capabilities.data?.placement_enabled === true;
  // A manual-only plan has no PO to review and goes straight to project start (USA-1388).
  const operationsComplete = Boolean(
    plan?.proposed_pos.every((proposedPO) =>
      ["approved", "placed"].includes(proposedPO.state),
    ),
  );
  const activeReviewView =
    activeSalesChangeRequest || !plan || reviewView === "sales"
      ? "sales"
      : reviewView === "project" ||
          (reviewView === "auto" && operationsComplete)
        ? "project"
        : "operations";
  const effectiveBlockingCount = draftReview.blockingCount;
  const nonOverrideableBlockingCount = Math.max(
    0,
    effectiveBlockingCount - minimumMarginIssues.length,
  );
  const hasMinimumMarginOverride = minimumMarginIssues.length > 0;
  const primaryDisabled = plan
    ? false
    : planExpected ||
      !approvalsEnabled ||
      !canSalesApprove ||
      nonOverrideableBlockingCount > 0;
  const primaryLabel = plan
    ? "Review generated purchase orders"
    : activeSalesChangeRequest
      ? hasMinimumMarginOverride
        ? "Approve below minimum margin"
        : "Approve changes"
      : approvalsEnabled
        ? hasMinimumMarginOverride
          ? "Approve below minimum margin"
          : "Approve"
        : "Approvals are not enabled";
  const preBuildQuoteAgentAvailable =
    ready &&
    !lineDataLoading &&
    !lineQuery.error &&
    shell.root_line_count === 0;
  const quoteAgentAvailable =
    shell.source_kind === "ai_document" ||
    Boolean(aiBuild.data) ||
    clarificationQuestions.length > 0 ||
    preBuildQuoteAgentAvailable;

  const openLineDialog = (parent: DropshipV2Line | null) => {
    setLineDialogParent(parent);
    setLineDialogOpen(true);
  };

  const openQuoteAgent = () => {
    setQuoteAgentExplicitlyOpened(true);
    setQuoteAgentOpen(true);
  };

  const submitSalesApproval = (
    minimumMarginOverrideReason?: string,
    closeOverride?: () => void,
  ) => {
    salesApproval.mutate(
      {
        contentRevision: shell.content_revision,
        note: salesNote.trim() || undefined,
        minimumMarginOverrideReason,
      },
      {
        onSuccess: () => {
          setMinimumMarginOverrideOpen(false);
          closeOverride?.();
        },
        onError: (error) =>
          toast.error("Sales approval blocked", {
            description: dropshipV2ErrorMessage(
              error,
              "Resolve validation issues and retry.",
            ),
          }),
      },
    );
  };

  const reviewRetainedEquipment = useCallback(() => {
    setQuoteAgentOpen(false);
    window.setTimeout(() => {
      const target = document.getElementById("dropship-retained-equipment");
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
      target?.focus();
    }, 0);
  }, []);

  return (
    <div className="mx-auto max-w-[1600px] space-y-5 p-4 md:p-6">
      <header className="border-b pb-4">
        <Button variant="ghost" size="sm" asChild className="-ml-3 mb-2">
          <Link to="/projects/dropship-drafts">
            <ArrowLeft className="size-4" /> Dropship drafts
          </Link>
        </Button>
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="min-w-0 text-xl font-semibold sm:text-2xl">
                {fields.isLoading ? (
                  <Skeleton className="h-8 w-72" />
                ) : !ready ? (
                  quoteTitle
                ) : (
                  <InlineDraftField
                    draftUuid={draftUuid}
                    field={byKey.get("quote.project_name")}
                    label="Project name"
                    placeholder={quoteTitle}
                    hideLabel
                    buttonClassName="min-h-9 px-1 text-xl font-semibold sm:text-2xl"
                  />
                )}
              </h1>
              <Badge>
                {shell.workflow_state === "pm_review"
                  ? "PO review"
                  : formatStatus(shell.workflow_state)}
              </Badge>
            </div>
          </div>
          {ready || quoteAgentAvailable ? (
            <div className="flex flex-wrap items-center justify-end gap-2">
              {quoteAgentAvailable ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-h-11 sm:min-h-9"
                  onClick={openQuoteAgent}
                >
                  <Sparkles className="size-4 text-usa-blue" /> Quote agent
                </Button>
              ) : null}
              {ready && plan && !activeSalesChangeRequest ? (
                <div
                  role="group"
                  aria-label="Review view"
                  className="inline-flex rounded-lg border bg-muted/40 p-1"
                >
                  <Button
                    type="button"
                    variant={
                      activeReviewView === "sales" ? "secondary" : "ghost"
                    }
                    size="sm"
                    className="min-h-11 sm:min-h-9"
                    aria-pressed={activeReviewView === "sales"}
                    onClick={() => setReviewView("sales")}
                  >
                    Sales review
                  </Button>
                  <Button
                    type="button"
                    variant={
                      activeReviewView === "operations" ? "secondary" : "ghost"
                    }
                    size="sm"
                    className="min-h-11 sm:min-h-9"
                    aria-pressed={activeReviewView === "operations"}
                    onClick={() => setReviewView("operations")}
                  >
                    PO review
                  </Button>
                  <Button
                    type="button"
                    variant={
                      activeReviewView === "project" ? "secondary" : "ghost"
                    }
                    size="sm"
                    className="min-h-11 sm:min-h-9"
                    aria-pressed={activeReviewView === "project"}
                    disabled={!operationsComplete}
                    onClick={() => setReviewView("project")}
                  >
                    Project review
                  </Button>
                </div>
              ) : null}
              {ready ? (
                <ReviewOwners draftUuid={draftUuid} shell={shell} />
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      {!ready ? (
        <>
          {renderProcessing()}
          {showRetainedEquipment ? (
            <section
              id="dropship-retained-equipment"
              tabIndex={-1}
              aria-labelledby="dropship-retained-equipment-title"
              className="scroll-mt-6 space-y-3 rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
            >
              <div>
                <h2
                  id="dropship-retained-equipment-title"
                  className="text-base font-semibold"
                >
                  Retained equipment
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Read-only until retry completes
                </p>
              </div>
              <DropshipLineItemTable
                groups={displayedGroups}
                sortMode={sortMode}
                loadedRootCount={groups.length}
                totalRootCount={shell.root_line_count}
                hasMore={Boolean(lineQuery.hasNextPage)}
                loadingMore={lineQuery.isFetchingNextPage}
                loadMoreError={lineQuery.isFetchNextPageError}
                manufacturerLoading={
                  sortMode === "manufacturer" &&
                  !lineQuery.isFetchNextPageError &&
                  (Boolean(lineQuery.hasNextPage) ||
                    lineQuery.isFetchingNextPage)
                }
                readOnly
                onSortModeChange={setSortMode}
                onEdit={(line, changes: DropshipV2LineChanges) =>
                  linePatch.mutate({ line, changes })
                }
                onAddRoot={() => openLineDialog(null)}
                onAddChild={openLineDialog}
                onRemove={(line) => lineRemoval.mutate({ line })}
                onLoadMore={() => void lineQuery.fetchNextPage()}
              />
            </section>
          ) : null}
        </>
      ) : activeReviewView !== "sales" && plan ? (
        renderPlanWorkflow({
          plan,
          canPMApprove,
          canPlace,
          fields: fields.data ?? [],
          stage: activeReviewView,
          onOperationsComplete: () => setReviewView("project"),
        })
      ) : (
        <>
          {activeSalesChangeRequest ? (
            <section
              role="status"
              aria-labelledby="sales-change-request-title"
              className="flex min-w-0 items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-4 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100"
            >
              <AlertCircle
                className="mt-0.5 size-5 shrink-0"
                aria-hidden="true"
              />
              <div className="min-w-0">
                <h2
                  id="sales-change-request-title"
                  className="text-sm font-semibold"
                >
                  {activeSalesChangeRequest.manufacturer_name} PO reopened
                </h2>
                <p className="mt-1 break-words text-sm leading-6">
                  {activeSalesChangeRequest.reason}
                </p>
              </div>
            </section>
          ) : null}
          {fields.isLoading ? (
            <Skeleton className="h-56 w-full rounded-xl" />
          ) : (
            <QuoteDetailStrip
              draftUuid={draftUuid}
              fields={fields.data ?? []}
            />
          )}

          <DropshipFreightReview
            draftUuid={draftUuid}
            fields={fields.data ?? []}
            review={freightReview.data}
            loading={freightReview.isLoading}
            fetching={freightReview.isFetching}
            error={freightReview.error}
            disabled={planExpected}
            onReload={() => void freightReview.refetch()}
          />

          <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
            <div className="min-w-0 space-y-4">
              {lineDataLoading && groups.length === 0 ? (
                <div className="overflow-hidden rounded-xl border">
                  <Skeleton className="h-16 w-full rounded-none" />
                  <Skeleton className="mt-px h-24 w-full rounded-none" />
                  <Skeleton className="mt-px h-24 w-full rounded-none" />
                </div>
              ) : groups.length === 0 &&
                (lineQuery.error || shell.root_line_count > 0) ? (
                <div
                  role="alert"
                  className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-xl border border-destructive/30 px-5 text-center text-sm text-destructive"
                >
                  <AlertCircle className="size-5" aria-hidden="true" />
                  <p>
                    {dropshipV2ErrorMessage(
                      lineQuery.error,
                      "Equipment lines could not be loaded.",
                    )}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="min-h-11 sm:min-h-9"
                    disabled={lineQuery.isFetching}
                    onClick={() => void lineQuery.refetch()}
                  >
                    {lineQuery.isFetching ? (
                      <Loader2 className="size-4 animate-spin" />
                    ) : null}
                    Retry equipment lines
                  </Button>
                </div>
              ) : (
                <DropshipLineItemTable
                  groups={displayedGroups}
                  sortMode={sortMode}
                  loadedRootCount={groups.length}
                  totalRootCount={shell.root_line_count}
                  hasMore={Boolean(lineQuery.hasNextPage)}
                  loadingMore={lineQuery.isFetchingNextPage}
                  loadMoreError={lineQuery.isFetchNextPageError}
                  manufacturerLoading={
                    sortMode === "manufacturer" &&
                    !lineQuery.isFetchNextPageError &&
                    (Boolean(lineQuery.hasNextPage) ||
                      lineQuery.isFetchingNextPage)
                  }
                  readOnly={!ready}
                  reviewableMatchLineUuids={reviewableMatchLineUuids}
                  pendingLineUuids={pendingLineUuids}
                  removingLineUuid={
                    lineRemoval.isPending
                      ? lineRemoval.variables?.line.uuid
                      : undefined
                  }
                  revealLineId={revealedLineId}
                  revealNonce={revealNonce}
                  revealManufacturerKey={revealedManufacturerKey}
                  lineBlockers={lineBlockers}
                  renderManufacturer={(line) => (
                    <ManufacturerAssignmentPicker
                      currentId={line.manufacturer_id}
                      currentName={line.manufacturer_name}
                      className="min-h-7 px-1 py-0 text-xs"
                      pending={linePatch.isPending}
                      allowCreate
                      onChange={(manufacturer) =>
                        linePatch.mutate({
                          line,
                          changes: {
                            manufacturer_id: manufacturer.id,
                            manufacturer_name: manufacturer.name,
                          },
                        })
                      }
                    />
                  )}
                  renderSource={(line) => (
                    <WarehouseSourcePicker
                      value={line.warehouse_source}
                      options={
                        line.product_id != null
                          ? warehouseStock.data?.get(line.product_id)
                          : undefined
                      }
                      lineLabel={`Line ${line.display_number}`}
                      readOnly={!ready}
                      pending={
                        linePatch.isPending &&
                        linePatch.variables?.line.uuid === line.uuid
                      }
                      onChange={(source) =>
                        linePatch.mutate({
                          line,
                          changes: { warehouse_source: source },
                        })
                      }
                    />
                  )}
                  renderManufacturerDetails={(bucket) => (
                    <DropshipManufacturerFreightReview
                      draftUuid={draftUuid}
                      fields={fields.data ?? []}
                      review={freightReview.data}
                      manufacturerId={bucket.manufacturerId}
                      manufacturerName={bucket.name}
                      rootLineUuids={bucket.rootLineUuids}
                      disabled={planExpected}
                      fetching={freightReview.isFetching}
                    />
                  )}
                  onSortModeChange={setSortMode}
                  onEdit={(line, changes: DropshipV2LineChanges) =>
                    linePatch.mutate({ line, changes })
                  }
                  onAddRoot={() => openLineDialog(null)}
                  onAddChild={openLineDialog}
                  onReviewMatch={(line) => setSelectedMatchLineUuid(line.uuid)}
                  onRemove={(line) => lineRemoval.mutate({ line })}
                  onLoadMore={() => void lineQuery.fetchNextPage()}
                  emptyState={
                    preBuildQuoteAgentAvailable ? (
                      <DropshipAIQuoteUpload
                        draftUuid={draftUuid}
                        contentRevision={shell.content_revision}
                        onManualAdd={() => openLineDialog(null)}
                        onBuildStarted={(build) => {
                          setTrackedAIBuildUuid(build.uuid);
                          setQuoteAgentExplicitlyOpened(true);
                          setQuoteAgentOpen(true);
                          toast.success("Quote agent started");
                        }}
                      />
                    ) : undefined
                  }
                />
              )}
            </div>

            <div className="xl:sticky xl:top-6">
              {issues.isLoading || fields.isLoading ? (
                <Skeleton className="h-[520px] w-full rounded-xl" />
              ) : (
                <ReviewApprovalPanel
                  title={
                    activeSalesChangeRequest
                      ? "Sales reapproval"
                      : "Review and approval"
                  }
                  checks={checks}
                  blockingCount={effectiveBlockingCount}
                  totals={[
                    {
                      label: "Merchandise",
                      value: formatMoney(
                        fieldText(byKey, "quote.merchandise_total") || "0",
                      ),
                    },
                    {
                      label: "Freight Net",
                      value:
                        freightReview.data?.pricing.freight_net == null
                          ? "-"
                          : formatMoney(freightReview.data.pricing.freight_net),
                    },
                    {
                      label: "Freight Sell",
                      value:
                        freightReview.data?.pricing.freight_sell == null
                          ? "Pending"
                          : formatMoney(
                              freightReview.data.pricing.freight_sell,
                            ),
                    },
                    {
                      label: "Tax",
                      value: (
                        <InlineDraftField
                          draftUuid={draftUuid}
                          field={byKey.get("quote.tax_total")}
                          label="Tax"
                          kind="money"
                          hideLabel
                          displayValue={formatMoney(
                            fieldText(byKey, "quote.tax_total") || "0",
                          )}
                          buttonClassName="justify-end px-1 font-medium tabular-nums"
                        />
                      ),
                    },
                    {
                      label: "Quote total",
                      value: formatMoney(
                        fieldText(byKey, "quote.total") || "0",
                      ),
                      emphasized: true,
                    },
                  ]}
                  totalsNote=""
                  note={salesNote}
                  onNoteChange={
                    !plan && !planExpected && approvalsEnabled
                      ? setSalesNote
                      : undefined
                  }
                  primaryLabel={primaryLabel}
                  primaryDisabled={primaryDisabled}
                  primaryPending={salesApproval.isPending}
                  primaryHint={
                    plan
                      ? undefined
                      : planExpected
                        ? planQuery.error
                          ? "Retry the purchase order plan before continuing review."
                          : "Review stays locked until the existing purchase order plan is available."
                        : !approvalsEnabled
                          ? "Draft review and editing remain available while approval rollout is disabled."
                          : hasMinimumMarginOverride &&
                              nonOverrideableBlockingCount > 0
                            ? `Fix ${nonOverrideableBlockingCount} other blocker${nonOverrideableBlockingCount === 1 ? "" : "s"} first`
                            : undefined
                  }
                  footer={
                    plan ? (
                      <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:bg-emerald-950/30 dark:text-emerald-300">
                        <CheckCircle2 className="size-4" /> Sales approved
                      </div>
                    ) : undefined
                  }
                  wrapPrimary={(button) =>
                    hasMinimumMarginOverride && !plan ? (
                      <ActionPopover
                        anchorOnly
                        open={minimumMarginOverrideOpen}
                        onOpenChange={setMinimumMarginOverrideOpen}
                        trigger={button}
                        title="Approve below minimum margin?"
                        note={{ label: "Reason", minLength: 1 }}
                        confirmLabel="Approve below minimum"
                        pending={salesApproval.isPending}
                        pendingLabel="Approving..."
                        onConfirm={(reason, close) =>
                          submitSalesApproval(reason, close)
                        }
                      >
                        <p className="text-xs text-muted-foreground">
                          {minimumMarginIssues.length === 1
                            ? "One quote line is below its required margin."
                            : `${minimumMarginIssues.length} quote lines are below their required margin.`}
                        </p>
                      </ActionPopover>
                    ) : (
                      button
                    )
                  }
                  onPrimaryAction={() => {
                    if (plan) {
                      setReviewView("operations");
                      return;
                    }
                    if (planExpected) return;
                    if (hasMinimumMarginOverride) {
                      setMinimumMarginOverrideOpen(true);
                      return;
                    }
                    submitSalesApproval();
                  }}
                />
              )}
            </div>
          </div>
        </>
      )}

      <LineCreateDialog
        key={`${lineDialogParent?.uuid ?? "root"}-${lineDialogOpen ? "open" : "closed"}`}
        draftUuid={draftUuid}
        shell={shell}
        open={lineDialogOpen}
        parent={lineDialogParent}
        onOpenChange={setLineDialogOpen}
      />
      {quoteAgentAvailable ? (
        <DropshipQuoteAgent
          draftUuid={draftUuid}
          build={aiBuild.data}
          lines={groups.map((group) => group.root)}
          buildLoading={aiBuild.isLoading}
          buildError={aiBuild.error}
          mutationsDisabled={planExpected || !ready}
          open={quoteAgentOpen}
          explicitlyOpened={quoteAgentExplicitlyOpened}
          selectedLine={selectedMatchLine}
          focusClarificationUuid={focusedClarificationUuid}
          onClarificationFocused={() => setFocusedClarificationUuid(null)}
          onReviewRetainedEquipment={
            showRetainedEquipment ? reviewRetainedEquipment : undefined
          }
          onOpenChange={setQuoteAgentOpen}
          onSelectedLineChange={(line) =>
            setSelectedMatchLineUuid(line?.uuid ?? null)
          }
        />
      ) : null}
    </div>
  );
}
